import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { consumptionAdjustmentService } from "~/services/production";

import type { MermaBatchInput, OverconsumptionInput } from "~/types/production";

const BATCH_KEY = "production_batches";
const MERMAS_KEY = "ingredient_mermas";
const OVERCONSUMPTIONS_KEY = "batch_overconsumptions";

type QueryClient = ReturnType<typeof useQueryClient>;

function invalidateStockAndCosting(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["oil_types"] });
  qc.invalidateQueries({ queryKey: ["raw_materials"] });
  qc.invalidateQueries({ queryKey: ["oil_type_stock_by_plant"] });
  qc.invalidateQueries({ queryKey: ["raw_material_stock_by_plant"] });
  qc.invalidateQueries({ queryKey: ["costing"] });
  qc.invalidateQueries({ queryKey: [MERMAS_KEY] });
  qc.invalidateQueries({ queryKey: [OVERCONSUMPTIONS_KEY] });
}

function invalidateBatch(qc: QueryClient, batchId: string) {
  qc.invalidateQueries({ queryKey: [BATCH_KEY] });
  qc.invalidateQueries({ queryKey: [BATCH_KEY, batchId] });
  qc.invalidateQueries({ queryKey: [BATCH_KEY, batchId, "ingredient_costs"] });
}

export function useRecordOverconsumption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OverconsumptionInput) =>
      consumptionAdjustmentService.recordOverconsumption(input),
    onSuccess: (_res, input) => {
      invalidateBatch(qc, input.batch_id);
      invalidateStockAndCosting(qc);
    },
  });
}

export function useReverseOverconsumption() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pbiId }: { pbiId: string; batchId: string }) =>
      consumptionAdjustmentService.reverseOverconsumption(pbiId),
    onSuccess: (_res, { batchId }) => {
      invalidateBatch(qc, batchId);
      invalidateStockAndCosting(qc);
    },
  });
}

export function useRecordMermas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MermaBatchInput) =>
      consumptionAdjustmentService.recordMermas(input),
    onSuccess: (_res, input) => {
      if (input.batch_id) invalidateBatch(qc, input.batch_id);
      invalidateStockAndCosting(qc);
    },
  });
}

export function useReverseMerma() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (movementId: string) =>
      consumptionAdjustmentService.reverseMerma(movementId),
    onSuccess: () => invalidateStockAndCosting(qc),
  });
}

export function useMermas(filters?: {
  period?: string;
  plant?: string;
  ingredientId?: string;
}) {
  return useQuery({
    queryKey: [MERMAS_KEY, filters],
    queryFn: () => consumptionAdjustmentService.getMermas(filters),
  });
}

export function useOverconsumptions(filters?: {
  period?: string;
  plant?: string;
}) {
  return useQuery({
    queryKey: [OVERCONSUMPTIONS_KEY, filters],
    queryFn: () => consumptionAdjustmentService.getOverconsumptions(filters),
  });
}
